import {
  calculateQuestionCost,
  calculateQuestionCostByToken,
  calculateProcessCost,
  formatTime,
  newFormatTime,
  formatDateTime,
} from '../../utils/calcs';
import { mapSentimentAndColors, sentimentTranslations } from '../../utils/mappings';
import {
  getDeterministicSpeakerColor,
  createSpeakerColorMapping,
} from '../../utils/speakerColors';
import { PIPELINE_STATUSES, PIPELINE_STEPS, STEP_DETAILS } from '../../utils/pipelineHelpers';

function assert(condition, message) {
  if (!condition) throw new Error(message);
}

export const utilityTests = [
  {
    id: 'util-calc-1',
    name: 'calculateQuestionCost(3600) === 8',
    run: async () => {
      const result = calculateQuestionCost(3600);
      assert(result === 8, `Expected 8, got ${result}`);
    },
  },
  {
    id: 'util-calc-2',
    name: 'calculateQuestionCost(0) === 0',
    run: async () => {
      const result = calculateQuestionCost(0);
      assert(result === 0, `Expected 0, got ${result}`);
    },
  },
  {
    id: 'util-calc-3',
    name: 'calculateQuestionCost("abc") === 0',
    run: async () => {
      const result = calculateQuestionCost('abc');
      assert(result === 0, `Expected 0, got ${result}`);
    },
  },
  {
    id: 'util-calc-4',
    name: 'calculateQuestionCostByToken(1000) === 2',
    run: async () => {
      const result = calculateQuestionCostByToken(1000);
      assert(result === 2, `Expected 2, got ${result}`);
    },
  },
  {
    id: 'util-calc-5',
    name: 'calculateProcessCost(3600) === 100',
    run: async () => {
      const result = calculateProcessCost(3600);
      assert(result === 100, `Expected 100, got ${result}`);
    },
  },
  {
    id: 'util-calc-6',
    name: 'formatTime(90) === "1m:30s"',
    run: async () => {
      const result = formatTime(90);
      assert(result === '1m:30s', `Expected "1m:30s", got "${result}"`);
    },
  },
  {
    id: 'util-calc-7',
    name: 'formatTime(0) === "-"',
    run: async () => {
      const result = formatTime(0);
      assert(result === '-', `Expected "-", got "${result}"`);
    },
  },
  {
    id: 'util-calc-8',
    name: 'newFormatTime(3661) === "01:01:01"',
    run: async () => {
      const result = newFormatTime(3661);
      assert(result === '01:01:01', `Expected "01:01:01", got "${result}"`);
    },
  },
  {
    id: 'util-calc-9',
    name: 'formatDateTime returns string with year',
    run: async () => {
      const result = formatDateTime('2023-06-15T10:30:00');
      assert(typeof result === 'string' && result.includes('2023'), `Expected string with "2023", got "${result}"`);
    },
  },
  {
    id: 'util-calc-10',
    name: 'formatDateTime(null) === "Date not available"',
    run: async () => {
      const result = formatDateTime(null);
      assert(result === 'Date not available', `Expected "Date not available", got "${result}"`);
    },
  },
  {
    id: 'util-mapping-1',
    name: 'mapSentimentAndColors("Engagement") === "green-600"',
    run: async () => {
      const result = mapSentimentAndColors('Engagement');
      assert(result === 'green-600', `Expected "green-600", got "${result}"`);
    },
  },
  {
    id: 'util-mapping-2',
    name: 'mapSentimentAndColors("Frustration") === "red-600"',
    run: async () => {
      const result = mapSentimentAndColors('Frustration');
      assert(result === 'red-600', `Expected "red-600", got "${result}"`);
    },
  },
  {
    id: 'util-mapping-3',
    name: 'sentimentTranslations("Engagement", "pt") === "Foco"',
    run: async () => {
      const result = sentimentTranslations('Engagement', 'pt');
      assert(result === 'Foco', `Expected "Foco", got "${result}"`);
    },
  },
  {
    id: 'util-speaker-1',
    name: 'getDeterministicSpeakerColor("Alice") returns valid color string',
    run: async () => {
      const result = getDeterministicSpeakerColor('Alice');
      assert(typeof result === 'string' && result.length > 0, `Expected non-empty string, got "${result}"`);
    },
  },
  {
    id: 'util-speaker-2',
    name: 'getDeterministicSpeakerColor("") === "gray-500"',
    run: async () => {
      const result = getDeterministicSpeakerColor('');
      assert(result === 'gray-500', `Expected "gray-500", got "${result}"`);
    },
  },
  {
    id: 'util-speaker-3',
    name: 'createSpeakerColorMapping(["Alice","Bob"]) has 2 keys',
    run: async () => {
      const result = createSpeakerColorMapping(['Alice', 'Bob']);
      const keys = Object.keys(result);
      assert(keys.length === 2, `Expected 2 keys, got ${keys.length}`);
      assert('Alice' in result, 'Expected "Alice" key');
      assert('Bob' in result, 'Expected "Bob" key');
    },
  },
  {
    id: 'util-pipeline-1',
    name: 'PIPELINE_STATUSES has PENDING, COMPLETED, FAILED',
    run: async () => {
      assert(PIPELINE_STATUSES.PENDING === 'pending', `PENDING should be "pending"`);
      assert(PIPELINE_STATUSES.COMPLETED === 'completed', `COMPLETED should be "completed"`);
      assert(PIPELINE_STATUSES.FAILED === 'failed', `FAILED should be "failed"`);
    },
  },
  {
    id: 'util-pipeline-2',
    name: 'STEP_DETAILS has entry for every PIPELINE_STEPS key',
    run: async () => {
      const stepKeys = Object.values(PIPELINE_STEPS);
      for (const stepKey of stepKeys) {
        assert(STEP_DETAILS[stepKey] !== undefined, `Missing STEP_DETAILS entry for "${stepKey}"`);
      }
    },
  },
];
